import React, { useMemo, useState } from "react";
import { Card, Paper, Toolbar, Container, Typography, Button, Divider, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Menu, MenuItem } from "@mui/material"
import PersonIcon from '@mui/icons-material/Person';
import { urls, partition } from "urls";
import { getEnvS3Name } from "./ApplicantDetailsFooter/ApplicantDetailsFooterData";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAudit, useExpectedResponse } from "hooks";
import { Auth } from "aws-amplify";
import { ApplicantDetailsStyle } from "../../../customStyles/ApplicantDetailsStyles";
import { useApplicantDetailsStore, useUserStore } from "state";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { useToastAlert } from "common/alerts";
import { DEFAULT_AGENCY_STATUS_MAP } from "utils/queryTransactionsHelpers";

interface ApplicantDetailsHeaderProps {
  userRole: string,
  userProfile: any,
  applicantImageUrl: string,
  applicantFetchStatus: string
  registrationId: string
  applicantData: any
  usersPermissions: any
  aggregatedResponses: any
  resultsResponses: any
  transactionalInfo: any
  onResubmitSuccess: () => void
}


export const ApplicantDetailsHeader: React.FC<ApplicantDetailsHeaderProps> = ({
  usersPermissions,
  userRole,
  userProfile,
  applicantImageUrl,
  applicantFetchStatus,
  applicantData,
  registrationId,
  aggregatedResponses,
  resultsResponses,
  transactionalInfo,
  onResubmitSuccess,
}) => {
  console.log(`ApplicantDetailsHeader props: `, {
    usersPermissions,
    userRole,
    userProfile,
    applicantImageUrl,
    applicantFetchStatus,
    applicantData,
    registrationId,
    aggregatedResponses,
    resultsResponses,
    transactionalInfo
  })


  let s3bucket = getEnvS3Name(`${process.env.REACT_APP_STAGE}`)!;
  const { receivedResponses, expectedResponse } = useExpectedResponse(registrationId, resultsResponses);
  const allResponsesReceived = expectedResponse === receivedResponses;
  const [openModalNotification, setOpenNotification] = useState(false);
  const workflowUrl = urls.WORKFLOW
  const [displayDecisionAlert, setDisplayDecisionAlert] = useState(false);
  const { notifySuccess, notifyError } = useToastAlert();
  const applicantHeader = useApplicantDetailsStore(state => state.applicantHeader);
  const incompleteStatuses = useApplicantDetailsStore(state => state.incompleteStatuses)
  const config = useUserStore(state => state.config);
  const { nistParsing } = config;
  const fingerprintQualityOptionsList = config?.fingerprintQualityOptionsList ?? DEFAULT_AGENCY_STATUS_MAP;
  const manualAdjudicationLabel = config?.manualAdjudicationLabel ?? 'FINAL DECISION';
  const manualAdjudicationTriggerStatus = config?.manualAdjudicationTriggerStatus  ?? ['Waiting for Manual Adjudication'];
  const incomingTCN = nistParsing.fields.find(item => item.name === "incomingTCN");
  const url = urls.TRANSACTION
  const { addAuditEvent } = useAudit();

  const [ssnValues, setSsnValues] = useState({
    SSN: userProfile.ssn,
    showSSN: false
  });


  const handleClickShowSSN = () => {
    setSsnValues({ ...ssnValues, showSSN: !ssnValues.showSSN })
  }

  const handleResubmission = async () => {
    setOpenNotification(false);
    const res = await resubmission();
    console.log("debug handleResubmission handleExtend res: ", res.message);
    if (res.message === "Successfully fired API") {
      notifySuccess("Transaction Resubmitted", "Transaction submitted to AFIS")
      addAuditEvent("Resubmitted Record", "Details / Adjudication Document", new Date(), registrationId)
      setTimeout(() => onResubmitSuccess(), 3000);
    } else {
      notifyError("Transaction Resubmission Failed", "Please contact the system administrator")
    }
  };

  const resubmission = async () => {
    const { username } = await Auth.currentAuthenticatedUser();

    const endpoint = `${process.env.NODE_ENV === 'production' ? workflowUrl : ""}/resubmitTransaction`;

    const body = {
      "Records": [
        {
          "person_id": `${applicantData.parentid}`, //parent_registration_id from association
          "isResubmission": "true",
          "generateTCN": false,
          "s3_bucket_nist": `${s3bucket}`,
          "s3_key_nist": `${applicantData.attachments[0].displayid}`,
          "registration_id": `${registrationId}`,
          "tcn": `${applicantData.tcn}`,
          "tcr": applicantData.descriptors.data[`type${incomingTCN.type}`][incomingTCN.key] || "",
          "user": username,
          "s3": {
            "bucket": {
              "name": `${s3bucket}`,
              "arn": `arn:${partition}:s3:::${s3bucket}`
            },
            "object": {
              "key": `${applicantData.attachments[0].displayid}`
            }
          }
        }

      ]
    };


    console.log('debugging resub applicant data', { applicantData, body })


    const options: RequestInit = {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(body),
    };
    try {
      const response = await (await fetch(endpoint, options)).json();
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const handleCloseModal = () => {
    setOpenNotification(false);
  };

  const handleOpenModal = () => {
    setOpenNotification(true)
  }

  const ResubmitModal = (open) => {
    return (
      <Dialog open={open} color='background.default' >
        <DialogTitle sx={{ width: '37.5rem' }}>ARE YOU SURE YOU WANT TO RE-SUBMIT?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. Select 'Yes, Re-submit' to continue, or 'No, cancel' if you would like to return.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pr: '1.5rem', pb: '1rem' }}>
          <Button variant='outlined' color='primary' sx={{}} onClick={handleCloseModal}>NO, CANCEL</Button>
          <Button variant='contained' color='primary' onClick={handleResubmission}>YES, RE-SUBMIT</Button>
        </DialogActions>
      </Dialog>
    )
  }

  console.log('usersPermissions?.canResubmitTransaction', usersPermissions?.canResubmitTransaction)

  console.log('env vars: ', process.env)

  const fingerprintQualityOptions = useMemo(() => {
    if (fingerprintQualityOptionsList && typeof fingerprintQualityOptionsList === 'object' && Object.keys(fingerprintQualityOptionsList).length > 0) {
      const adjudicationOptionsByCardType = config?.adjudicationOptionsByCardType;
      let optionsList = fingerprintQualityOptionsList;

      if (adjudicationOptionsByCardType) {
        const cardType = transactionalInfo?.cardType;

        if (cardType && adjudicationOptionsByCardType.overrides?.[cardType]) {
          // Card type has an override — show only those options
          const allowedKeys: string[] = adjudicationOptionsByCardType.overrides[cardType];
          optionsList = Object.fromEntries(
            Object.entries(fingerprintQualityOptionsList).filter(([key]) => allowedKeys.includes(key))
          );
        } else if (adjudicationOptionsByCardType.excludeFromDefault) {
          // No override match — exclude options that are only for overridden card types
          const excludedKeys: string[] = adjudicationOptionsByCardType.excludeFromDefault;
          optionsList = Object.fromEntries(
            Object.entries(fingerprintQualityOptionsList).filter(([key]) => !excludedKeys.includes(key))
          );
        }
      }

      return Object.entries(optionsList).map(([key, value]) => ({
        label: key,
        value: (value as { id: string }).id
      }));
    }
    throw new Error(`fingerprintQualityOptionsList is undefined cannot display options`)
  }, [fingerprintQualityOptionsList, transactionalInfo, config])


  const [decisionText, setDecisionText] = useState("")

  const updateDecision = async (e: any) => {
    const decision = e.target.innerText
    console.log('updateDecision decision: ', decision)
     //Adding Logic to handle non-termianl 
    const { username } = await Auth.currentAuthenticatedUser()
    let endpoint = `${process.env.NODE_ENV === 'production' ? url : ""}/transaction/taskResponse/${registrationId}`;
    let body: { user?: string; decision?: string, transactionStatus?:string } = {
      user: username,
      decision
    };
    if (!decision) return;
    setAnchorEl(null)


    const selectedOption = Object.values(fingerprintQualityOptionsList).find(
      (opt: any) => opt.id === decision
    );

    if (selectedOption && !(selectedOption as { id: string; isfinal: boolean }).isfinal) {
        endpoint = `${process.env.NODE_ENV === 'production' ? url : ""}/applicant/${registrationId}`;
        body ={
          transactionStatus:(selectedOption as { id: string }).id
        }

    }

    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(body)
    };

    console.log('updateDecision body: ', body)

    fetch(endpoint, options).then(async (response: Response) => {
      if (response.ok) {
        setDecisionText(decision)
        setDisplayDecisionAlert(true)
        notifySuccess(
          "Decision Submitted",
          `${manualAdjudicationLabel}: ${decision}`
        )
        setTimeout(() => setDisplayDecisionAlert(false), 4000)
        await addAuditEvent(
          `Manual Adjudication ${manualAdjudicationLabel}: ${decision}`,
          `Applicant Details Page`,
          new Date(),
          registrationId,
        );
        window.location.reload();
      } else throw new Error(`HTTP error: ${response.status}`)
    }).catch(() => {
      notifyError("Request Failed", "Please contact the system administrator.")
    });
  };

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopover = ((e: any) => {
    setAnchorEl(e.currentTarget);
  })

  console.log('debugging tcn: ', { userProfile, applicantData, incompleteStatuses, resultsResponses, expectedResponse, resubDisabled: usersPermissions?.canResubmitTransaction ? resultsResponses?.length !== expectedResponse && incompleteStatuses?.includes(userProfile?.status) ? false : true : true })

  return (
    <Card sx={ApplicantDetailsStyle.card} elevation={0} data-testid="applicant-details-header-card">
      <Paper sx={ApplicantDetailsStyle.paper} elevation={0}>
        <Toolbar sx={ApplicantDetailsStyle.toolBar}>
          <Container maxWidth={false} disableGutters sx={ApplicantDetailsStyle.container}>
            <Typography variant="h5" color='text.primary' sx={{ width: '100%' }} height="max-content" >{userProfile.name}</Typography>
            {usersPermissions?.canResubmitTransaction &&
              <Button onClick={handleOpenModal}
                disabled={!incompleteStatuses?.includes(userProfile?.status)}
                sx={ApplicantDetailsStyle.resubmitButton}
                size="medium"
                color="primary"
                variant="outlined"
                data-testid="re-submit-transaction-button"
                aria-label="RE-SUBMIT TRANSACTION">RE-SUBMIT TRANSACTION</Button>
            }
            {
              //Removed previous expected results check and flag because the backend handles putting the transaction in this status.
              //Its in the adjudication config that handles if the program does manual adjudication. The improvement here would be to add
              // manual adjudication permission and the status check so only authorized users are able to manually adjudicate 
              usersPermissions?.allowManualAdjudication && allResponsesReceived && manualAdjudicationTriggerStatus.includes(userProfile.status) && <>
                <Button disabled={displayDecisionAlert} onClick={handlePopover} sx={{ fontSize: decisionText === "No Record Found" ? "0.7324rem" : "0.875rem", height: "36px", minWidth: "180px" }} endIcon={<ArrowDropDownIcon />}
                  size="medium" color="secondary" variant="contained" aria-label={manualAdjudicationLabel}>{decisionText ? decisionText : manualAdjudicationLabel}</Button>
                <Menu
                  elevation={0}
                  PaperProps={{
                    style: {
                      boxShadow: '0px 3px 5px -1px rgba(124, 124, 130, 0.20)',

                    }
                  }}
                  sx={{ borderRadius: '0.375rem', }}
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  //anchorPosition={{ top: 50, left: 140 }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center"
                  }}>
                  {
                    fingerprintQualityOptions.map((option) => {
                      return <MenuItem onClick={(e) => updateDecision(e)} sx={{ minWidth: "180px" }} value={option.value as string}>{option.value as string}</MenuItem>
                    })
                  }
                </Menu>
              </>
            }
          </Container>
        </Toolbar>
        <Divider aria-hidden role="separator" />
        {openModalNotification && <ResubmitModal />}
        {/* <ResubmitModal open={openModalNotification}/> */}
        <Container maxWidth={false} disableGutters sx={ApplicantDetailsStyle.modalContainer}>
          <Box sx={{ width: '200px', maxWidth: '200px', height: '160px', maxHeight: '160px', overflow: 'hidden', flexShrink: 0 }}>
            {
              applicantImageUrl?.length
                ? <img style={{ maxWidth: "200px", maxHeight: "160px", objectFit: "contain", display: "block" }}
                  src={applicantImageUrl}
                  alt='Applicant'
                  loading="lazy"
                />
                : <PersonIcon sx={{ height: "100%", width: "100%" }} />
            }
          </Box>
          <Container sx={ApplicantDetailsStyle.demographicsContainer} maxWidth={false} disableGutters>
            {applicantHeader.map((header: any) => {
              return (
                <Container maxWidth={false} disableGutters sx={{ display: "flex", width: "100%" }} data-testid="applicant-detail-header-info">
                  <Typography sx={{ width: '11.5rem' }} variant='body2' color='text.secondary'>
                    {header.label}
                  </Typography>
                  <Typography variant='body2' color='text.primary'
                    sx={{
                      fontSize: header.key === "ssn" && userProfile.ssn !== "N/A"
                        ? ssnValues.showSSN ? "0.875rem"
                          : "1.3rem" : "",
                      lineHeight: header.key === "ssn" && userProfile.ssn !== "N/A" ? 0 : ""
                    }}>
                    {header.key === "ssn" && userProfile.ssn !== "N/A"
                      ? ssnValues.showSSN ? userProfile.ssn : "---------"
                      : header.key === "secondaryOri"
                        ? userProfile[header.key] ? userProfile[header.key]
                          : "N/A" : userProfile[header.key]
                    }
                    {usersPermissions?.viewAllDetailsData && header.key === "ssn" &&
                      <Tooltip title={ssnValues.showSSN ? "Hide" : "Show"}>
                        <IconButton size="small"
                          aria-label="Toggle password visibility"
                          onClick={handleClickShowSSN}
                          edge="end"
                          data-testid={ssnValues.showSSN ? "visibility-off-icon" : "visibility-on-icon"}
                        >
                          {header.key === "ssn" && userProfile.ssn !== "N/A" ? ssnValues.showSSN ? <VisibilityOff sx={{ fontSize: '15px' }} /> : <Visibility sx={{ fontSize: '15px' }} /> : null}
                        </IconButton>
                      </Tooltip>
                    }
                  </Typography>
                </Container>
              )
            })}
          </Container>
        </Container>
      </Paper>
    </Card>
  )
}
